/**
 * Clipboard HTML → sanitized inline HTML.
 *
 * Without this, a paste inserts the clipboard's RAW HTML — divs and
 * paragraphs with their own margins, spans with font-size/line-height
 * styles — and the block's typography goes haywire (the "huge vertical
 * space" bug). Notion-style rule: pasted content adopts OUR styling.
 *
 * Kept: text, basic inline formatting (bold/italic/underline/strike/code),
 * links (href only), and line breaks (as <br>). Everything else — style
 * attributes, spans, block wrappers, images, classes — is dropped.
 */

import { escapeHtml, escapeAttr } from './htmlEscape';
import { isSafeHref } from './linkSafety';
import { pillRefFromTag } from '../variableUtils/pillHtml';

// tag → tag it serializes as (normalizing B→strong-style variants to one form)
const INLINE_TAGS = {
  B: 'b',
  STRONG: 'b',
  I: 'i',
  EM: 'i',
  U: 'u',
  S: 's',
  STRIKE: 's',
  DEL: 's',
  CODE: 'code',
};

// Elements that imply a line break after their content
const BLOCK_TAGS = new Set([
  'P', 'DIV', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'TR', 'TABLE', 'BLOCKQUOTE', 'PRE', 'SECTION', 'ARTICLE', 'HEADER',
  'FOOTER', 'FIGURE', 'HR',
]);

/**
 * Browsers often rewrite <code> to a <span style="font-family:…"> when
 * copying from contenteditable. Treat those as inline code on paste.
 */
const isMonospaceLike = (el) => {
  const style = (el.getAttribute?.('style') || '').toLowerCase();
  const cls = (el.getAttribute?.('class') || '').toLowerCase();
  const face = (el.getAttribute?.('face') || '').toLowerCase();
  const haystack = `${style} ${cls} ${face}`;
  return /monospace|consolas|courier|cascadia|fira.?code|menlo|monaco|source.?code|jetbrains.?mono|ui.?monospace|\bcode\b/.test(
    haystack
  );
};

/** Same idea for bold/italic — Chrome loves <span style="font-weight:700">. */
const isBoldLike = (el) => {
  const style = (el.getAttribute?.('style') || '').toLowerCase();
  return /font-weight:\s*(bold|[6-9]00)/.test(style);
};
const isItalicLike = (el) => {
  const style = (el.getAttribute?.('style') || '').toLowerCase();
  return /font-style:\s*italic/.test(style);
};

const wrapInline = (tag, inner) => {
  if (!inner) return '';
  // Avoid <code><code>…</code></code> when a monospace span sat inside <code>
  if (tag === 'code') {
    const trimmed = inner.trim();
    if (/^<code>[\s\S]*<\/code>$/i.test(trimmed)) return trimmed;
  }
  return `<${tag}>${inner}</${tag}>`;
};

/** Recursively reduce a DOM node to sanitized inline HTML with \n markers. */
const sanitizeNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    // Drop ZWSP caret buffers that trail variable pills
    return escapeHtml((node.textContent || '').replace(/\u200B/g, ''));
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const tag = node.tagName;
  if (tag === 'BR') return '\n';
  // Non-content elements
  if (tag === 'STYLE' || tag === 'SCRIPT' || tag === 'HEAD' || tag === 'TITLE' || tag === 'META') {
    return '';
  }

  let inner = '';
  node.childNodes.forEach((child) => {
    inner += sanitizeNode(child);
  });

  const inlineTag = INLINE_TAGS[tag];
  if (inlineTag) {
    return wrapInline(inlineTag, inner);
  }

  if (tag === 'A') {
    const href = node.getAttribute('href');
    if (href && inner && isSafeHref(href)) {
      return `<a href="${escapeAttr(href.trim())}">${inner}</a>`;
    }
    return inner;
  }

  // Math pills — keep the formula even when unresolved so paste-back works.
  if (tag === 'SPAN' && node.hasAttribute('data-doc-math')) {
    const expr = (node.getAttribute('data-math-expr') || '').trim();
    const state = node.getAttribute('data-var-state') || 'unresolved';
    const style = (node.getAttribute('style') || '').trim();
    const text = node.textContent || expr || 'math';
    const safeStyle = /--var-color\s*:/.test(style)
      ? style
          .split(';')
          .map((s) => s.trim())
          .filter((s) => /^--var-color\s*:/.test(s))
          .join('; ')
      : '';
    const styleAttr = safeStyle
      ? ` style="${escapeAttr(safeStyle.endsWith(';') ? safeStyle : `${safeStyle};`)}"`
      : '';
    return (
      `<span class="doc-variable" data-doc-math data-math-expr="${escapeAttr(expr)}" ` +
      `data-var-state="${escapeAttr(state)}" contenteditable="false" spellcheck="false"${styleAttr}>` +
      `${escapeHtml(text)}</span>`
    );
  }

  // Preserve in-editor variable pills. Keep display state/color/text when
  // present — rebuilding via buildVariableHtml() always forced "unresolved"
  // (red name), which made table cells flicker on every keystroke/paste.
  // Drop data-var-tip on the wire: it's cosmetic, refreshed on render, and
  // its "{{name}} - From …" text used to get pill-ified mid-attribute on paste.
  // Empty/unresolved pills are invisible on copy (they only show the name).
  if (tag === 'SPAN' && node.hasAttribute('data-doc-variable')) {
    const name = (node.getAttribute('data-var-name') || '').trim();
    if (!name) return '';
    const state = node.getAttribute('data-var-state') || 'unresolved';
    if (state === 'empty' || state === 'unresolved') return '';
    const style = (node.getAttribute('style') || '').trim();
    const text = node.textContent || name;
    const rawIndex = (node.getAttribute('data-var-index') || '').trim();
    const indexAttr =
      rawIndex === '*'
        ? ' data-var-index="*"'
        : rawIndex && /^\d+$/.test(rawIndex) && parseInt(rawIndex, 10) >= 1
          ? ` data-var-index="${escapeAttr(rawIndex)}"`
          : '';
    // Only allow our cosmetic style var — drop anything else from paste
    const safeStyle = /--var-color\s*:/.test(style)
      ? style
          .split(';')
          .map((s) => s.trim())
          .filter((s) => /^--var-color\s*:/.test(s))
          .join('; ')
      : '';
    const styleAttr = safeStyle ? ` style="${escapeAttr(safeStyle.endsWith(';') ? safeStyle : `${safeStyle};`)}"` : '';
    return (
      `<span class="doc-variable" data-doc-variable data-var-name="${escapeAttr(name)}"${indexAttr} ` +
      `data-var-state="${escapeAttr(state)}" contenteditable="false" spellcheck="false"${styleAttr}>` +
      `${escapeHtml(text)}</span>`
    );
  }

  // Styled spans Chrome emits instead of real tags when copying from the editor
  if (tag === 'SPAN' || tag === 'FONT') {
    if (isMonospaceLike(node)) return wrapInline('code', inner);
    if (isBoldLike(node)) return wrapInline('b', inner);
    if (isItalicLike(node)) return wrapInline('i', inner);
  }

  if (BLOCK_TAGS.has(tag)) {
    return inner ? `${inner}\n` : '';
  }

  // Unknown/other elements (span, font, mark, td…): keep content, drop wrapper
  return inner;
};

/**
 * Inline HTML → markdown-ish plain text so paste paths that prefer text/plain
 * (e.g. checkbox lists) still keep `code`, **bold**, *italic*.
 */
const variablePillToken = (node) => {
  const name = (node.getAttribute('data-var-name') || '').trim();
  if (!name) return '';
  const rawIndex = (node.getAttribute('data-var-index') || '').trim();
  if (rawIndex === '*') return `{{${name}[*]}}`;
  const index = rawIndex && /^\d+$/.test(rawIndex) ? parseInt(rawIndex, 10) : null;
  return index != null && index >= 1 ? `{{${name}[${index}]}}` : `{{${name}}}`;
};

/**
 * Inline HTML → markdown-ish plain text.
 * @param {string} html
 * @param {{ variablesAsValues?: boolean, resolveVariable?: Function }} [options]
 *   variablesAsValues — emit the resolved value instead of `{{name}}`.
 *   resolveVariable — when set, copy the live value (pill text may be truncated).
 *   Used by callout Copy; default keeps tokens for paste-back.
 */
export const inlineHtmlToMarkdown = (html, { variablesAsValues = false, resolveVariable = null } = {}) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const walk = (node) => {
    // Drop ZWSP caret buffers that sit after variable pills
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || '').replace(/\u200B/g, '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const tag = node.tagName;
    if (tag === 'BR') return '\n';
    let inner = '';
    node.childNodes.forEach((child) => {
      inner += walk(child);
    });
    if (tag === 'CODE') return `\`${inner}\``;
    if (tag === 'B' || tag === 'STRONG') return `**${inner}**`;
    if (tag === 'I' || tag === 'EM') return `*${inner}*`;
    // Variable pills: tokens by default (paste can rebuild); optional values.
    // Empty/unresolved pills only show the name in the UI — omit from copy.
    if (tag === 'SPAN' && node.hasAttribute('data-doc-math')) {
      const state = node.getAttribute('data-var-state') || 'unresolved';
      if (state === 'empty' || state === 'unresolved') return '';
      return inner;
    }
    if (tag === 'SPAN' && node.hasAttribute('data-doc-variable')) {
      const state = node.getAttribute('data-var-state') || 'unresolved';
      if (state === 'empty' || state === 'unresolved') return '';
      if (variablesAsValues) {
        if (resolveVariable) {
          const value = String(resolveVariable(pillRefFromTag(node))?.value ?? '');
          if (!value.trim()) return '';
          return value;
        }
        return inner;
      }
      return variablePillToken(node) || inner;
    }
    if (tag === 'A') {
      const href = (node.getAttribute('href') || '').trim();
      if (href && isSafeHref(href)) {
        // Bare-URL links collapse to the URL; labeled ones keep [text](url)
        if (inner === href || inner === href.replace(/^https?:\/\//i, '')) {
          return href;
        }
        return `[${inner}](${href})`;
      }
    }
    return inner;
  };
  return walk(doc.body.firstElementChild || doc.body);
};

/** Clipboard HTML → sanitized inline HTML (line breaks become <br>). */
export const sanitizeClipboardHtml = (html) => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  const raw = sanitizeNode(doc.body);
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line, i, arr) => line !== '' || (i > 0 && arr[i - 1] !== '')) // collapse blank runs
    .join('<br>')
    .replace(/^(<br>)+|(<br>)+$/g, '');
};

/** Plain text → escaped inline HTML (newlines become <br>). */
export const sanitizeClipboardText = (text) =>
  escapeHtml(text || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n/g, '<br>');
